import {
	EditQuizFooterProps,
	EditQuizStep,
	RenderableProctoringKey,
	VerityAssessmentPageName,
	getEditQuizStepName,
	useEditQuizButton,
} from "@verity/components";
import { coreToast, StepConfig } from "@verity/components/core";
import { VerityAssessmentType } from "@verity/database/prisma";
import { useCallback, useContext, useMemo, useState } from "react";
import { PortalControllerContext } from "../../../../context";
import { VerityAssessmentSectionProps } from "..";
import { UseEditQuizStepReturn } from "./useEditQuizStep";
import {
	AssessmentSaveError,
	baseTransKey,
	deleteAssessmentResource,
	getCourse,
	getFileDiff,
	getInitialCompletedSteps,
	saveVerityAssessment,
	uploadAssessmentResource,
} from "../verityAssessmentHelper";
import { UseFetchVerityAssessmentInfoReturn } from "./useFetchVerityAssessmentInfo";
import { AssessmentInfo } from "@verity/api-client/types";
import { AssessmentDetailsReturn } from "./useAssessmentDetails";
import { AddInstructionsReturn } from "./useAddInstructions";
import { QuestionDeliveryReturn } from "./useQuestionDelivery";
import { QuizQuestionsReturn } from "./useQuizQuestions";
import i18n from "../../../../../i18n";
import useValidateVerityAssessmentPage from "./useValidateVerityAssessment";

type UseVerityAssessmentFlowProps = {
	isEditing: boolean;
	assessmentType: VerityAssessmentType;
	editQuizConfig: UseEditQuizStepReturn;
	fetchConfig: UseFetchVerityAssessmentInfoReturn;
	assessmentDetails: AssessmentDetailsReturn;
	addInstructions: AddInstructionsReturn;
	questionDelivery: QuestionDeliveryReturn;
	quizQuestions: QuizQuestionsReturn;
} & VerityAssessmentSectionProps;

/**Stepper, navigation, save button */
export default function useVerityAssessmentFlow({
	isEditing,
	assessmentType,
	assessment,
	editQuizConfig,
	fetchConfig,
	assessmentDetails,
	addInstructions,
	questionDelivery,
	quizQuestions,
	onFinish,
	verityAssessment,
}: UseVerityAssessmentFlowProps) {
	const { portalContext } = useContext(PortalControllerContext);
	const { lms, instituteIntegrationHostname, lmsProvidedCourseId } = portalContext;

	const [step, setStep] = useState<number>(0);
	const [completedSteps, setCompletedSteps] = useState<number[]>(getInitialCompletedSteps(isEditing));
	const [saving, setSaving] = useState(false);
	const [lmsQuizId, setLmsQuizId] = useState<string | undefined>(assessment?.quizId);

	const { editQuizRef, updateEditQuizData, isFinalEditQuizPage, isFirstEditQuizPage, enforcedProfilesOnly } =
		editQuizConfig;
	const { courses, instructorFiles, modules, loading } = fetchConfig;
	const { questions, setSelectedIndex } = quizQuestions;

	const { backButton, buttonHandlers, nextButton, saveButton, saveProfileButton } = useEditQuizButton();

	const errors = useValidateVerityAssessmentPage({
		assessmentDetails,
		addInstructions,
		assessmentType,
		courses,
		modules,
		questions,
	});

	const stepNames: VerityAssessmentPageName[] = useMemo(() => {
		const steps: VerityAssessmentPageName[] = [];

		if (!isEditing) {
			steps.push("assessmentType");
		}
		steps.push("assessmentDetails");
		steps.push(assessmentType === "quiz" ? "questions" : "instructions");
		steps.push("editQuiz");
		steps.push("review");

		return steps;
	}, [assessmentType, isEditing]);

	const stepConfig = useMemo(() => {
		const config: StepConfig<VerityAssessmentPageName>[] = [];

		stepNames.forEach((stepName) => {
			config.push({
				key: stepName,
				label: getEditQuizStepName(stepName),
			});
		});
		return config;
	}, [stepNames]);

	const curStepName = useMemo(() => stepNames[step], [stepNames, step]);

	const getStepIndexByName = useCallback(
		(stepName: VerityAssessmentPageName) => {
			const index = stepNames.findIndex((name) => name === stepName);
			return index === -1 ? 0 : index;
		},
		[stepNames],
	);

	const handleSave = useCallback(
		async (publish: boolean) => {
			try {
				setSaving(true);

				// This just updates EditQuizWrapper with the latest data from EditQuiz. See defaultSave in useEditQuizSubmit
				const saveSuccessful = await editQuizRef.current?.saveButtonHandler();
				if (!saveSuccessful) {
					setSaving(false);
					return;
				}

				const updatedEditQuizData = updateEditQuizData();

				const cleanedCourseId = lms
					? getCourse(courses, assessmentDetails.course.selectedCourseId)?.lmsCourseId
					: lmsProvidedCourseId;

				if (!cleanedCourseId) {
					throw new AssessmentSaveError("NO_COURSE");
				}

				const quizInfo: AssessmentInfo = {
					lms,
					hostname: instituteIntegrationHostname,
					courseId: cleanedCourseId,
					quizId: lmsQuizId ?? crypto.randomUUID().replaceAll("-", ""),
					quizInfo: {
						name: assessmentDetails.generalInfo.assessmentTitle,
					},
				};

				// Get updated quizIds after saving to db
				const quizIds = await saveVerityAssessment({
					assessmentDetails,
					editQuizData: updatedEditQuizData,
					quizInfo,
					addInstructions,
					questionDelivery,
					type: assessmentType,
					quizQuestions: questions,
					publish,
				});

				setLmsQuizId(quizIds.lmsQuizId);

				if (assessmentType === "assignment" && addInstructions.uploadedFiles.length > 0) {
					const { addedFiles, removedFiles } = getFileDiff(instructorFiles, addInstructions.uploadedFiles);
					const quizId = quizIds.verityQuizId;
					for (const file of removedFiles) {
						await deleteAssessmentResource(quizId, file.name);
					}

					for (const file of addedFiles) {
						if (file.nativeFile) {
							await uploadAssessmentResource(file.nativeFile, quizId);
						}
					}
				}

				const successMessageKey = isEditing
					? "successToastUpdate"
					: publish
						? "successToastPublish"
						: "successToastNoPublish";

				coreToast.dismiss();
				coreToast.success(i18n.t(`${baseTransKey}.${successMessageKey}`) as string, {
					autoClose: 2500,
				});
				onFinish();
			} catch (e) {
				console.error(e);
				coreToast.dismiss();

				if (e instanceof AssessmentSaveError) {
					coreToast.error(i18n.t(`${baseTransKey}.errorToast.${e.code}`) as string, {
						autoClose: 2500,
					});
					return;
				}
				coreToast.error(i18n.t(`${baseTransKey}.errorToast.DB_FAIL`) as string, {
					autoClose: 2500,
				});
			} finally {
				setSaving(false);
			}
		},
		[
			addInstructions,
			assessmentDetails,
			assessmentType,
			courses,
			editQuizRef,
			instituteIntegrationHostname,
			instructorFiles,
			isEditing,
			lms,
			lmsProvidedCourseId,
			lmsQuizId,
			onFinish,
			questionDelivery,
			questions,
			updateEditQuizData,
		],
	);

	type NavigationSource = "next" | "back" | "stepper";

	const navigateToStep = useCallback(
		(
			targetStep: number,
			source: NavigationSource,
			editQuizStep?: EditQuizStep,
			proctoringOptionToFocus?: RenderableProctoringKey,
		) => {
			// If we are at verityConfig, and next/back button is clicked, delegate to next/back button handler because they handle configuration page logic
			if (curStepName === "editQuiz" && source !== "stepper") {
				// Going out of edit quiz step
				const isNextAndIsFinalEditQuizPage = source === "next" && isFinalEditQuizPage;
				const isBackAndIsFirstEditQuizPage = source === "back" && isFirstEditQuizPage;

				if (!isNextAndIsFinalEditQuizPage && !isBackAndIsFirstEditQuizPage) {
					editQuizRef.current?.[source === "next" ? "nextButtonHandler" : "backButtonHandler"]();
					return; // skip the rest of the logic, since the handlers, handles it
				}
			}

			if (targetStep === step) return;

			const goingToEditQuizStep = getStepIndexByName("editQuiz") === targetStep;

			// Make sure everything is validated before jumping to steps handled by ref
			if (goingToEditQuizStep) {
				let stepKey: VerityAssessmentPageName | undefined;

				if (errors.assessmentDetailsErrors.length > 0) {
					stepKey = "assessmentDetails";
				} else if (errors.addInstructionErrors.length > 0) {
					stepKey = "instructions";
				} else if (errors.questionsErrors.length > 0) {
					stepKey = "questions";
				}

				if (stepKey) {
					coreToast.dismiss();
					if (stepKey === "questions") {
						coreToast.error(
							errors.questionsErrors[0].description ||
								(i18n.t(`${baseTransKey}.verityAssessmentStepFailToast`, {
									stepName: getEditQuizStepName(stepKey),
								}) as string),
							{
								autoClose: 2500,
							},
						);

						setSelectedIndex(errors.questionsErrors[0].questionIndex ?? 0);
					} else {
						coreToast.error(
							i18n.t(`${baseTransKey}.verityAssessmentStepFailToast`, {
								stepName: getEditQuizStepName(stepKey),
							}) as string,
							{
								autoClose: 2500,
							},
						);
					}

					setStep(getStepIndexByName(stepKey));
					return;
				}
			}

			// Update edit quiz data when going out past verityConfig step
			if (curStepName === "editQuiz" || targetStep === getStepIndexByName("review")) {
				updateEditQuizData();
			}

			setCompletedSteps((prev) => {
				const nextCompleted = new Set(prev);

				if (targetStep > step) {
					nextCompleted.add(step);
				}

				return Array.from(nextCompleted);
			});
			setStep(targetStep);

			if (goingToEditQuizStep) {
				// If going back to edit quiz step via back button, start on last step aka Accommodation Step
				if (source === "back") {
					editQuizRef.current?.goToPage("accommodations");
				} else if (editQuizStep) {
					editQuizRef.current?.goToPage(editQuizStep ?? "method", proctoringOptionToFocus);
				}
			}
		},
		[
			curStepName,
			step,
			getStepIndexByName,
			isFinalEditQuizPage,
			isFirstEditQuizPage,
			editQuizRef,
			errors.assessmentDetailsErrors.length,
			errors.addInstructionErrors.length,
			errors.questionsErrors,
			setSelectedIndex,
			updateEditQuizData,
		],
	);

	// For the CoreStepper. since it expects a function of number -> void
	const handleGoToStep = useCallback(
		(targetStep: number) => {
			navigateToStep(targetStep, "stepper");
		},
		[navigateToStep],
	);

	const handleBack = useCallback(() => {
		navigateToStep(step - 1, "back");
	}, [navigateToStep, step]);

	const handleNext = useCallback(() => {
		navigateToStep(step + 1, "next");
	}, [navigateToStep, step]);

	const handleOnEdit = useCallback(
		(
			step: VerityAssessmentPageName,
			editQuizStep?: EditQuizStep,
			proctoringOptionToFocus?: RenderableProctoringKey,
		) => {
			navigateToStep(getStepIndexByName(step), "stepper", editQuizStep, proctoringOptionToFocus);
		},
		[getStepIndexByName, navigateToStep],
	);

	const isNextDisabled = useMemo(() => {
		if (loading || saving) return true;

		if (curStepName === "assessmentDetails") {
			return errors.assessmentDetailsErrors.length > 0;
		}
		if (curStepName === "instructions") {
			return errors.addInstructionErrors.length > 0;
		}

		if (curStepName === "editQuiz") {
			return !nextButton.enabled && !isFinalEditQuizPage;
		}

		return false;
	}, [
		loading,
		saving,
		curStepName,
		nextButton.enabled,
		isFinalEditQuizPage,
		errors.assessmentDetailsErrors.length,
		errors.addInstructionErrors.length,
	]);

	const isBackDisabled = useMemo(
		() => step === 0 || (curStepName === "editQuiz" && !backButton.enabled && !isFirstEditQuizPage) || loading,
		[backButton.enabled, curStepName, loading, step, isFirstEditQuizPage],
	);

	const isSaveDisabled = useMemo(() => {
		if (loading || saving) return true;

		const isAssessmentDetailsOk = errors.assessmentDetailsErrors.length === 0;
		const isAddInstructionOk = errors.addInstructionErrors.length === 0;
		const isEditQuizOkay = saveButton.enabled;

		return !(isAssessmentDetailsOk && isAddInstructionOk && isEditQuizOkay);
	}, [errors.addInstructionErrors.length, errors.assessmentDetailsErrors.length, loading, saveButton.enabled, saving]);

	// Reusing edit quiz footer because it has the styles and translations
	// Probably can cleanup the save button logic
	const footerConfig = useMemo((): EditQuizFooterProps["slotProps"] => {
		const isReview = curStepName === "review";

		// If editing, use the current published status. Else its a "Save" button so no publish.
		const shouldPublish = isEditing ? !!verityAssessment?.isPublished : false;

		return {
			back: {
				onClick: handleBack,
				disabled: isBackDisabled,
			},
			// This becomes the "save" button on the review page
			next: {
				onClick: isReview ? () => handleSave(shouldPublish) : handleNext,
				disabled: isReview ? isSaveDisabled : isNextDisabled,
				loading: saving,
				text: isReview ? (i18n.t(`${baseTransKey}.save`) as string) : undefined,
				color: isReview ? "secondary" : "primary",
			},
			// This is the save and publish button on the review page
			// Does not show on edit
			confirm: {
				disabled: isSaveDisabled,
				onClick: () => handleSave(true),
				loading: saving,
				hide: !isReview || isEditing, // no save and publish when they are editing
				text: i18n.t(`${baseTransKey}.saveAndPublish`) as string,
			},
			// Show this on the last step of edit quiz so that we do not have 3 buttons on the review page
			saveProfile: {
				onClick: () => editQuizRef?.current?.saveAsProfile(),
				disabled: !saveProfileButton.enabled || enforcedProfilesOnly || saving,
				hide: curStepName !== "editQuiz" || !isFinalEditQuizPage || saveProfileButton.hide || enforcedProfilesOnly,
				loading: saving,
			},
		};
	}, [
		curStepName,
		editQuizRef,
		enforcedProfilesOnly,
		handleBack,
		handleNext,
		handleSave,
		isBackDisabled,
		isEditing,
		isFinalEditQuizPage,
		isNextDisabled,
		isSaveDisabled,
		saveProfileButton.enabled,
		saveProfileButton.hide,
		saving,
		verityAssessment?.isPublished,
	]);

	return {
		step,
		stepConfig,
		curStepName,
		footerConfig,
		completedSteps,
		handleGoToStep,
		handleOnEdit,
		buttonHandlers,
		lmsQuizId,
	};
}
